/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */
import { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Card,
    Stack,
    Typography,
    Button,
    CircularProgress,
    Divider,
    Chip,
    alpha,
} from '@mui/material';
import { Iconify } from '../../../components';
import { useSelector, dispatch } from '../../../redux/store';
// Canonical /form-deliveries/ slice (Track A.3 migration).
import {
    getLeadTreatmentPlans,
    clearLeadTreatmentPlans,
    getConsentForms,
    getTreatmentPlanTemplates,
} from '../../../redux/slices/formDeliveries';
import useSnackbar from '../../../hooks/useCustomSnackbar';
import TreatmentPlanCard from './TreatmentPlanCard';
import TreatmentPlanCurationDialog from './TreatmentPlanCurationDialog';

/**
 * Treatment Plans live in the Treatments tab as a sibling section to the
 * per-treatment accordion. They were previously surfaced inside the Forms tab
 * but conceptually belong with treatments — a treatment plan is the priced,
 * shareable representation of a treatment, not a generic patient form.
 *
 * Moving the section here also lets the Forms tab focus exclusively on
 * patient-facing form types (consent / medical history / surveys).
 */
export default function LeadTreatmentPlansSection({ leadDetail }) {
    const { enqueueSnackbar } = useSnackbar();

    const practiceId = useSelector((state) => state.practiceTreatmentList.practiceId);
    const {
        leadTreatmentPlans,
        leadTreatmentPlansLoading,
    } = useSelector((state) => state.formDeliveries);

    const [createPlanOpen, setCreatePlanOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);

    const leadId = leadDetail?.id;

    const loadPlans = useCallback(() => {
        if (!practiceId || !leadId) return;
        dispatch(getLeadTreatmentPlans(practiceId, leadId));
    }, [practiceId, leadId]);

    useEffect(() => {
        loadPlans();
        return () => {
            dispatch(clearLeadTreatmentPlans());
        };
    }, [loadPlans]);

    // Pre-fetch templates so the curation dialog opens instantly.
    useEffect(() => {
        if (!practiceId) return;
        dispatch(getConsentForms(practiceId, { form_type: 'TREATMENT_PLAN', limit: 50 }));
        dispatch(getTreatmentPlanTemplates(practiceId, { limit: 50 }));
    }, [practiceId]);

    const handleCopyLink = useCallback((plan) => {
        const link = plan?.link || plan?.form_link || '';
        if (link) {
            navigator.clipboard.writeText(link)
                .then(() => enqueueSnackbar('Link copied to clipboard', { variant: 'success' }))
                .catch(() => enqueueSnackbar('Could not copy link', { variant: 'error' }));
        } else {
            enqueueSnackbar('No link available yet', { variant: 'info' });
        }
    }, [enqueueSnackbar]);

    const handleEdit = useCallback((plan) => {
        setEditingPlan(plan);
        setCreatePlanOpen(true);
    }, []);

    const handleCurationClose = useCallback((refreshNeeded) => {
        setCreatePlanOpen(false);
        setEditingPlan(null);
        if (refreshNeeded) loadPlans();
    }, [loadPlans]);

    const plans = leadTreatmentPlans?.results || [];

    return (
        <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 3 }} />

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify icon="solar:document-medicine-bold-duotone" width={20} sx={{ color: '#00AB55' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '14px' }}>
                        Treatment Plans
                    </Typography>
                    {plans.length > 0 && (
                        <Chip
                            label={plans.length}
                            size="small"
                            sx={{ height: 18, fontSize: '10px', fontWeight: 700, bgcolor: 'rgba(0,171,85,0.1)', color: '#00AB55' }}
                        />
                    )}
                </Stack>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<Iconify icon="eva:plus-fill" width={14} />}
                    onClick={() => { setEditingPlan(null); setCreatePlanOpen(true); }}
                    sx={{
                        bgcolor: '#00AB55',
                        '&:hover': { bgcolor: '#007B40' },
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '12px',
                    }}
                >
                    Create Plan
                </Button>
            </Stack>

            {leadTreatmentPlansLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} sx={{ color: '#00AB55' }} />
                </Box>
            ) : plans.length > 0 ? (
                <Stack spacing={1.5}>
                    {plans.map((plan) => (
                        <TreatmentPlanCard
                            key={plan.id}
                            plan={plan}
                            onEdit={handleEdit}
                            onCopyLink={handleCopyLink}
                        />
                    ))}
                </Stack>
            ) : (
                <Card
                    sx={{
                        py: 4,
                        textAlign: 'center',
                        borderRadius: '12px',
                        border: '1px dashed rgba(0,171,85,0.2)',
                        bgcolor: 'rgba(0,171,85,0.02)',
                        boxShadow: 'none',
                    }}
                >
                    <Iconify icon="solar:document-medicine-bold-duotone" width={40} sx={{ color: 'rgba(0,171,85,0.3)', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5, fontSize: '13px' }}>
                        No treatment plans yet
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '12px' }}>
                        Create one to share treatment options and costs with your patient
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Iconify icon="eva:plus-fill" width={14} />}
                            onClick={() => { setEditingPlan(null); setCreatePlanOpen(true); }}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '12px',
                                borderColor: 'rgba(0,171,85,0.3)',
                                color: '#00AB55',
                                '&:hover': { bgcolor: alpha('#00AB55', 0.06) },
                            }}
                        >
                            Create First Plan
                        </Button>
                    </Box>
                </Card>
            )}

            {createPlanOpen && (
                <TreatmentPlanCurationDialog
                    open={createPlanOpen}
                    onClose={handleCurationClose}
                    leadDetail={leadDetail}
                    existingPlan={editingPlan}
                />
            )}
        </Box>
    );
}

LeadTreatmentPlansSection.propTypes = {
    leadDetail: PropTypes.object.isRequired,
};
